import Chevron from 'assets/icons/Chevron'
import { Badge } from 'components/Badge/Badge'
import { NavBar } from 'components/NavBar/NavBar'
import { useCallback, useMemo, useState } from 'react'
import { Button, ColoredDot, colors, Flex, H1WithBack, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, OtherText } from 'styling/Typography'
import {
  IZoneTreeNode,
  ZONE_STATUS_COLORS,
  ZONE_STATUS_LABELS,
} from 'types/zoneTree.interface'
import {
  ChevronSpacer,
  ChevronWrap,
  TreeList,
  TreeRow,
  TreeRowActions,
  TreeRowLabels,
  TreeRowMain,
} from './ZoneTree.styled'
import { ZONE_TREE_DATA } from './zoneTreeData'

const collectExpandableIds = (nodes: IZoneTreeNode[]): string[] => {
  const ids: string[] = []
  const walk = (node: IZoneTreeNode) => {
    if (node.children?.length) {
      ids.push(node.id)
      node.children.forEach(walk)
    }
  }
  nodes.forEach(walk)
  return ids
}

const ZoneStatusBadge = ({ status }: { status: IZoneTreeNode['status'] }) => {
  const color = ZONE_STATUS_COLORS[status]
  return (
    <Badge hexColor={color}>
      <Flex gap='0.5rem' center>
        <ColoredDot color={color} />
        <BodyText>{ZONE_STATUS_LABELS[status]}</BodyText>
      </Flex>
    </Badge>
  )
}

interface TreeNodeProps {
  node: IZoneTreeNode
  depth: number
  expandedIds: Set<string>
  onToggle: (id: string) => void
}

const TreeNode = ({ node, depth, expandedIds, onToggle }: TreeNodeProps) => {
  const hasChildren = Boolean(node.children?.length)
  const isExpanded = expandedIds.has(node.id)

  return (
    <>
      <TreeRow
        $clickable={hasChildren}
        onClick={hasChildren ? () => onToggle(node.id) : undefined}
      >
        <TreeRowMain $depth={depth}>
          {hasChildren ? (
            <ChevronWrap $open={isExpanded}>
              <Chevron gray />
            </ChevronWrap>
          ) : (
            <ChevronSpacer />
          )}
          <TreeRowLabels>
            <BodyText weight='600'>{node.name}</BodyText>
            <OtherText grey>{node.level}</OtherText>
          </TreeRowLabels>
        </TreeRowMain>
        <TreeRowActions>
          <ZoneStatusBadge status={node.status} />
          <Button small onClick={(e) => e.stopPropagation()}>
            Change
          </Button>
        </TreeRowActions>
      </TreeRow>
      {hasChildren &&
        isExpanded &&
        node.children?.map((child) => (
          <TreeNode
            key={child.id}
            node={child}
            depth={depth + 1}
            expandedIds={expandedIds}
            onToggle={onToggle}
          />
        ))}
    </>
  )
}

export const ZoneTree = () => {
  const expandableIds = useMemo(() => collectExpandableIds(ZONE_TREE_DATA), [])
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set())

  const onToggle = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const expandAll = () => setExpandedIds(new Set(expandableIds))
  const collapseAll = () => setExpandedIds(new Set())

  return (
    <>
      <NavBar />
      <PageWrapper>
        <H1WithBack arrow title='Zone Tree' />
        <Flex style={{ margin: '2rem 0' }} width='100%' between>
          <Flex gap='1rem'>
            <Flex gap='0.5rem' center>
              <ColoredDot color={colors.green} />
              <OtherText>Operational</OtherText>
            </Flex>
            <Flex gap='0.5rem' center>
              <ColoredDot color={colors.yellow} />
              <OtherText>Held</OtherText>
            </Flex>
            <Flex gap='0.5rem' center>
              <ColoredDot color={colors.red} />
              <OtherText>Evacuated</OtherText>
            </Flex>
          </Flex>
          <Flex gap='1rem'>
            <Button orangeBorder small onClick={expandAll}>
              Expand All
            </Button>
            <Button orangeBorder small onClick={collapseAll}>
              Collapse All
            </Button>
          </Flex>
        </Flex>
        <TreeList>
          {ZONE_TREE_DATA.map((zone) => (
            <TreeNode
              key={zone.id}
              node={zone}
              depth={0}
              expandedIds={expandedIds}
              onToggle={onToggle}
            />
          ))}
        </TreeList>
      </PageWrapper>
    </>
  )
}
